import { useState, type ReactNode } from 'react'
import { Minus, Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react'
import { formatCurrency } from '../../utils/format-currency'

interface ItemRowProps {
  id: string
  quantity: number
  name: string
  unitPrice?: number | null
  meta?: ReactNode
  detailContent?: ReactNode
  onIncrease?: () => void
  onDecrease?: () => void
  onRemove?: () => void
  disableIncrease?: boolean
  disableDecrease?: boolean
  disableActions?: boolean
}

export function ItemRow({
  quantity,
  name,
  unitPrice,
  meta,
  detailContent,
  onIncrease,
  onDecrease,
  onRemove,
  disableIncrease,
  disableDecrease,
  disableActions,
}: ItemRowProps) {
  const [expanded, setExpanded] = useState(false)
  const hasDetails = Boolean(detailContent)
  const canAdjust = Boolean(onIncrease || onDecrease)
  const showRemove = Boolean(onRemove)

  return (
    <div className="py-3 flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-sm font-medium text-[color:var(--color-text)] truncate">
              {quantity} × {name}
            </p>
            {typeof unitPrice === 'number' && (
              <p className="text-xs text-muted">{formatCurrency(unitPrice)}</p>
            )}
          </div>
          {canAdjust && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onDecrease}
                disabled={disableActions || disableDecrease}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-divider text-[color:var(--color-text)] hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Disminuir cantidad"
              >
                <Minus className="h-4 w-4" />
              </button>
              <span className="text-sm font-semibold text-[color:var(--color-text)]" aria-hidden="true">
                {quantity}
              </span>
              <button
                type="button"
                onClick={onIncrease}
                disabled={disableActions || disableIncrease}
                className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-divider text-[color:var(--color-text)] hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Aumentar cantidad"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          )}
        </div>
        {meta && <div className="mt-2 flex flex-wrap gap-2 text-xs text-[color:var(--color-text)]">{meta}</div>}
        {hasDetails && (
          <div className="mt-2">
            <button
              type="button"
              onClick={() => setExpanded((prev) => !prev)}
              className="flex items-center gap-1 text-xs font-medium text-muted hover:text-[color:var(--color-text)]"
              aria-expanded={expanded}
            >
              {expanded ? 'Ocultar detalles' : 'Ver detalles'}
              {expanded ? (
                <ChevronUp className="h-3 w-3" aria-hidden="true" />
              ) : (
                <ChevronDown className="h-3 w-3" aria-hidden="true" />
              )}
            </button>
            {expanded && (
              <div className="mt-2 space-y-1 text-xs text-muted" aria-live="polite">
                {detailContent}
              </div>
            )}
          </div>
        )}
      </div>
      {showRemove && (
        <button
          type="button"
          onClick={onRemove}
          disabled={disableActions}
          className="mt-1 inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full border border-divider text-[color:var(--color-text)] hover:bg-surface-muted disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Eliminar ítem"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      )}
    </div>
  )
}

export default ItemRow
